import React, { useState, useRef, useEffect } from 'react';
import Parcel from 'single-spa-react/parcel';
import { mountRootParcel } from 'single-spa';
import styles from './survey-file-upload.styles.css';
import {
  CprButton,
  CprIcon,
  CprLoader,
  CprTooltip,
  CpButton
} from 'canopy-styleguide!sofe';
import { catchAsyncStacktrace } from 'auto-trace';
import { useTaskAndFiles, useSofeService } from '../client-survey.helper';
import {
  addFilesToTask,
  getDocsToUpload
} from 'src/resources/tasks.resource.js';
import {
  getPrevSectionUrl,
  hasPrevious
} from '../../source-forms/sections/section.helper.js';
import { handleValidate } from '../../source-forms/questions/required.helpers.js';
import { forEach, isEmpty, get } from 'lodash';
import { Link, withRouter } from 'react-router-dom';

function SurveyFileClientUpload(props) {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [docsToUpload, setDocsToUpload] = useState([]);
  const uploadEl = useRef(null);
  const uploadAnotherEl = useRef(null);
  const docsUI = useSofeService('docs-ui!sofe');
  const [task, files, setFiles, fetching] = useTaskAndFiles(props.engagementId);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (isEmpty(props.sourceFormLayout.sections)) return;
    setSections(props.sourceFormLayout.sections);
    const subscription = getDocsToUpload(
      props.clientId,
      props.engagementId,
      props.version.id
    ).subscribe(result => {
      const relevantDocs = [];
      forEach(result, (value, key) => {
        const docName = key.replace('doc', '');
        if (key && key.startsWith('doc') && !!value) {
          relevantDocs.push({
            doc: value,
            info: get(result, `info${docName}`) || ''
          });
        }
      });
      setLoading(false);
      setDocsToUpload(relevantDocs);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [props.sourceFormLayout.formId]);

  const clearValue = e => {
    e.target.value = null;
  };

  const uploadFiles = newFiles => {
    docsUI.loadUploadFilesHelper().then(filesHelper => {
      filesHelper
        .uploadFilesAsync(newFiles, {
          destinationFolder: { id: `CON${props.clientId}` }
        })
        .then(responseFiles => {
          if (get(task, 'id')) {
            addFilesToTask(
              task.id,
              responseFiles.map(file => file.id)
            ).subscribe(
              result => {
                setUploading(false);
                setFiles([...files, ...responseFiles]);
              },
              err => {
                setUploading(false);
              }
            );
          } else {
            setUploading(false);
            setFiles([...files, ...responseFiles]);
          }
        });
    }, catchAsyncStacktrace());
  };

  return (
    <div className={`cps-card cps-fixed-focus ${styles.subsectionWrapper}`}>
      <div className="cps-padding-32">
        <div className="cps-header-sm">Upload Documents</div>
        <div className="cps-body" style={{ maxWidth: '568px' }}>
          Here are the documents we think you’ll need to upload. If there’s
          anything else your tax pro should look at, upload that here as well.
        </div>
        <div
          className="cps-margin-top-16"
          style={{ display: 'flex', position: 'relative' }}>
          <div
            ref={scrollContainerRef}
            style={{
              width: '339px',
              minHeight: '261px',
              maxHeight: '450px',
              overflowY: 'auto'
            }}
            className="cps-well cps-subheader-sm cps-padding-top-24 cps-padding-bottom-24 cps-padding-right-24">
            {fetching || loading ? (
              <div className="cps-center-vertical">
                <CprLoader />
              </div>
            ) : docsToUpload.length ? (
              <ul>
                {docsToUpload.map((doc, index) => {
                  return (
                    <li key={index}>
                      <span>{doc.doc}</span>
                      {doc.info && (
                        <CprTooltip
                          html={doc.info}
                          scrollContainerRef={scrollContainerRef.current}>
                          <CprIcon
                            name="information-circle-filled-small"
                            className="cps-color-monsoon"
                            style={{ marginTop: '-4px' }}
                          />
                        </CprTooltip>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div
                className={`${styles.emptyWell} cps-text-center cps-center-vertical`}>
                <div
                  className={`${styles.emptyText} cps-wt-bold cps-color-slate`}>
                  We need more information to recommend files
                </div>
                <div
                  className={`${styles.emptyText} cps-margin-top-8 cps-body-sm`}>
                  In order to view a list of required files, please complete
                  your personal information using the tabs on the left side of
                  this page.
                </div>
              </div>
            )}
          </div>
          {docsUI && (
            <div className={`cps-margin-left-16`} style={{ width: '339px' }}>
              {files.length === 0 ? (
                <Parcel
                  config={() => docsUI.loadDropListParcel()}
                  uploadFilesAction={files => {
                    setUploading(true);
                    uploadFiles([...files]);
                  }}
                  mountParcel={mountRootParcel}>
                  <div className={`cps-well_light ${styles.uploadDocsWell}`}>
                    <div className="cps-subheader-sm cps-color-slate cps-wt-bold">
                      Drag and Drop
                    </div>
                    <div className="cps-body">
                      <i>or</i>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <input
                        ref={uploadEl}
                        type="file"
                        name="uploadFile"
                        multiple
                        style={{ display: 'none' }}
                        onClick={clearValue}
                        onChange={e => {
                          setUploading(true);
                          uploadFiles([...e.target.files]);
                        }}
                      />
                      <CprButton
                        onClick={() => uploadEl.current.click()}
                        actionType="secondary"
                        disabled={uploading}
                        showLoader={uploading}>
                        Upload
                      </CprButton>
                    </div>
                  </div>
                </Parcel>
              ) : (
                <>
                  <div className="cps-wt-bold cps-margin-bottom-8">
                    Uploaded Documents
                  </div>
                  <div style={{ maxHeight: '334px', overflowY: 'auto' }}>
                    <React.Suspense fallback={<CprLoader />}>
                      <docsUI.LinkedFileListLazy
                        files={files}
                        cardWidth={'100%'}
                        cardClass={styles.fileCard}
                        isClientPortal={true}
                      />
                    </React.Suspense>
                  </div>
                  <Parcel
                    config={() => docsUI.loadDropListParcel()}
                    uploadFilesAction={files => {
                      setUploading(true);
                      uploadFiles([...files]);
                    }}
                    mountParcel={mountRootParcel}>
                    <div
                      className={`cps-well_light ${styles.uploadAnotherDocsWell}`}>
                      <div
                        className="cps-padding-top-24 cps-padding-bottom-24"
                        style={{ textAlign: 'center' }}>
                        <input
                          ref={uploadAnotherEl}
                          type="file"
                          name="uploadFile"
                          multiple
                          style={{ display: 'none' }}
                          onClick={clearValue}
                          onChange={e => {
                            setUploading(true);
                            uploadFiles([...e.target.files]);
                          }}
                        />
                        <CprButton
                          actionType="flat"
                          onClick={() => uploadAnotherEl.current.click()}
                          disabled={uploading}
                          showLoader={uploading}>
                          <CprIcon
                            className="cps-color-monsoon cps-margin-right-8"
                            name="misc-cloud-up-arrow"
                          />
                          <span className="cps-color-primary">
                            Upload another
                          </span>
                        </CprButton>
                      </div>
                    </div>
                  </Parcel>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        className={`cps-card__footer`}
        style={{ display: 'flex', justifyContent: 'center' }}>
        {sections && hasPrevious(sections, null, null) && (
          <Link
            to=""
            onClick={e =>
              handleValidate(e, () =>
                props.history.push(
                  `${props.baseUrl}${getPrevSectionUrl(sections, null, null)}`
                )
              )
            }>
            <CpButton btnType="secondary">Previous section</CpButton>
          </Link>
        )}
        {sections && (
          <Link
            to=""
            onClick={e =>
              handleValidate(e, () =>
                props.history.push(`${props.baseUrl}/submit`)
              )
            }>
            <CpButton className={`cp-ml-24`}>Next section</CpButton>
          </Link>
        )}
      </div>
    </div>
  );
}

export default withRouter(SurveyFileClientUpload);
